// 정산 payout 원본 행 → 표시용 넷팅 행 변환 + 관련 날짜 헬퍼.
// StoreSettlementStatement.jsx / StoreStatementLms.jsx가 공통으로 쓴다.

/** "YYYY-MM" → 그 달의 첫날/마지막날 "YYYY-MM-DD" */
export function monthToDateRange(yyyyMm) {
  if (!/^\d{4}-\d{2}$/.test(yyyyMm)) return null;
  const [y, m] = yyyyMm.split("-").map(Number);
  const start = `${yyyyMm}-01`;
  const lastDay = new Date(y, m, 0).getDate(); // m은 1-indexed라 다음달 0일=이번달 말일
  const end = `${yyyyMm}-${String(lastDay).padStart(2, "0")}`;
  return { start, end };
}

/** 기본 조회월 범위 — 지난달 ~ 이번달 */
export function getDefaultMonthRange() {
  const now = new Date();
  const toYm = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { startMonth: toYm(lastMonth), endMonth: toYm(now) };
}

export const OUR_COMPANY_STORE = "오스리움"; // 플랫폼 자사(수수료 수취) 상점명 — 가맹점 지급분과 분리 집계

/**
 * 보전 payout 종류 — 할인분(바우처/0원 쿠폰)을 상점에 보전해주는 행. 정산 데몬이 만드는 게 아니라
 * 운영자가 일회성으로 넣는 행이라(user_gift_nos 빈 배열, tossCharge 0) 오스리움 짝이 없다.
 * payout.amount 에 이미 보전액이 들어 있으므로 넷팅 순액(storeAmount)엔 자동 포함되고,
 * 아래 voucherCompAmount/couponCompAmount 는 그중 보전분이 얼마인지 보여주는 내역용이다.
 */
export const COMP_PAYOUT_TYPES = { VOUCHER: "VOUCHER", COUPON: "COUPON" };
export const isCompPayout = (p) =>
  p?.payoutType === COMP_PAYOUT_TYPES.VOUCHER || p?.payoutType === COMP_PAYOUT_TYPES.COUPON;

/**
 * [2026-10-20] 매장 검증(오프라인) 쿠폰 보전 행 — 기프트 없이 발급된 쿠폰을 매장에서 검증해 쓴 것 중
 * CHANNEL/PLATFORM 발행분을 정산 데몬이 상점별로 모아 쿠폰 액면 그대로 보전한 행(user_gift_nos 빈 배열,
 * tossCharge 0, 토스 결제 없음). 위 일회성 보전 행과 달리 comp_* 컬럼(발행주체·comp_detail.coupons)이 채워져 있다.
 */
export const OFFLINE_COUPON_PAYOUT_TYPE = "COUPON_OFF";
export const isOfflineCompPayout = (p) => p?.payoutType === OFFLINE_COUPON_PAYOUT_TYPE;
/** comp_detail 항목들 → 매장 검증 쿠폰 건수 */
export const offlineCouponCountOf = (details) =>
  (details ?? []).reduce((s, d) => s + (Array.isArray(d.coupons) ? d.coupons.length : 0), 0);

/**
 * [2026-10-20] 정산 데몬이 NORMAL/CLAWBACK 행에 같이 기록한 프로모션 할인 보전분(CLAWBACK은 음수).
 * payout.amount 에 이미 포함돼 있고 아래는 그 내역 — 종류 기준(voucher+coupon)과
 * 발행주체 기준(channel+platform) 합은 항상 같다. compDetail 항목은 api가 issuerName/promotionName을 붙여준다.
 */
export function compFieldsOf(p) {
  return {
    voucherCompAmount: Number(p?.compVoucherAmt ?? 0),
    couponCompAmount: Number(p?.compCouponAmt ?? 0),
    channelCompAmount: Number(p?.compChannelAmt ?? 0),
    platformCompAmount: Number(p?.compPlatformAmt ?? 0),
    // 상점 자체 발행 할인액 — 보전 아님(지급액·바우처/쿠폰 보전에 미포함), 기록용
    storeCompAmount: Number(p?.compStoreAmt ?? 0),
    compDetail: Array.isArray(p?.compDetail) ? p.compDetail : [],
  };
}

/**
 * compDetail 항목들 → 발행주체(채널은 채널별) 단위 합계 [{ key, label, amount, isStore }] (0원 제외).
 * STORE(상점 자체 할인)는 보전이 아니라 label 에 "(자체)"를 붙이고 isStore=true 로 구분한다.
 */
export function sumCompByIssuer(details) {
  const byKey = new Map();
  (details ?? []).forEach((d) => {
    const key = `${d.issuerType}:${d.issuerNo ?? ""}`;
    // const label =
    //   d.issuerType === "PLATFORM" ? "플랫폼" : d.issuerName || `채널 #${d.issuerNo ?? "?"}`;
    const isStore = d.issuerType === "STORE";
    const label =
      d.issuerType === "PLATFORM"
        ? "플랫폼"
        : isStore
          ? `${d.issuerName || "상점"} (자체)`
          : d.issuerName || `채널 #${d.issuerNo ?? "?"}`;
    const prev = byKey.get(key) ?? { key, label, amount: 0, isStore };
    prev.amount += Number(d.amount ?? 0);
    byKey.set(key, prev);
  });
  // 보전(채널·플랫폼) 먼저, 상점 자체 할인은 뒤로
  return [...byKey.values()].filter((x) => x.amount !== 0).sort((a, b) => Number(a.isStore) - Number(b.isStore));
}

/**
 * 표시용 행 만들기 — 2단계로 그룹화한다.
 * 1단계: userGiftNos가 같은 행끼리 짝짓기(상점 1건 + 오스리움 1건 = 같은 배치의 앞뒤 두 행).
 * 2단계: (storeNo, payoutPeriodKey)가 같은 배치들을 다시 합치기 — 실제 토스 송금 단위와 일치.
 */
export function mergeNettedRows(payouts) {
  const byGiftNos = new Map();
  // 보전 행은 1단계(userGiftNos 짝짓기)에서 뺀다 — 전부 빈 배열이라 한 그룹으로 뭉쳐 서로
  // 짝으로 잘못 묶일 수 있다. 2단계(storeNo+payoutPeriodKey)에서 같은 배치의 정산 행과 합쳐진다.
  const compPayouts = [];
  payouts.forEach((payout) => {
    // if (isCompPayout(payout)) {
    if (isCompPayout(payout) || isOfflineCompPayout(payout)) {
      compPayouts.push(payout);
      return;
    }
    const giftNosKey = JSON.stringify(payout.userGiftNos?.sort() || []);
    if (!byGiftNos.has(giftNosKey)) byGiftNos.set(giftNosKey, []);
    byGiftNos.get(giftNosKey).push(payout);
  });

  const batches = [];
  byGiftNos.forEach((group) => {
    const ourCompany = group.find(
      (p) => p.store?.storeName === OUR_COMPANY_STORE && Number(p.tossCharge || 0) === 0,
    );
    const store =
      group.find((p) => p.store?.storeName !== OUR_COMPANY_STORE) ??
      group.find((p) => p.store?.storeName === OUR_COMPANY_STORE && Number(p.tossCharge || 0) > 0);

    if (ourCompany && store) {
      const payoutType = store.payoutType ?? ourCompany.payoutType;
      const totalAmount = store.totalAmount || ourCompany.totalAmount || 0;
      // 환불 클로백 건의 원가격은 "환불된 정가"로 별도 추적 — 원가격 총액(totalAmount)에서
      // 이 값을 뺀 게 "환불 제외" 순수 판매분 정가.
      const refundFaceAmount = payoutType === "CLAWBACK" ? totalAmount : 0;
      const ourCompanyAmount = ourCompany.amount || 0;
      const tossCharge = store.tossCharge || 0;

      const storeDone = store.tossStatus === "COMPLETED" || !!store.manualPaidDttm;
      const ourCompanyDone =
        ourCompany.tossStatus === "COMPLETED" || !!ourCompany.manualPaidDttm;
      const bothDone = storeDone && ourCompanyDone;
      const anyTossCompleted =
        store.tossStatus === "COMPLETED" || ourCompany.tossStatus === "COMPLETED";

      batches.push({
        storeNo: store.storeNo,
        storeName: store.store?.storeName,
        accountHolder: store.store?.accountHolder,
        bankName: store.store?.bankName,
        accountNo: store.store?.accountNo,
        payoutMonth: store.payoutMonth ?? ourCompany.payoutMonth,
        payoutPeriodKey: store.payoutPeriodKey ?? ourCompany.payoutPeriodKey,
        tossPayoutDate: store.tossPayoutDate ?? ourCompany.tossPayoutDate,
        payoutNo: store.payoutNo,
        totalAmount,
        refundFaceAmount,
        ourCompanyAmount,
        storeAmount: store.amount,
        tossCharge,
        tossStatus: bothDone && anyTossCompleted ? "COMPLETED" : null,
        tossCompletedDttm: bothDone
          ? store.tossCompletedDttm || ourCompany.tossCompletedDttm
          : null,
        manualPaidDttm:
          bothDone && !anyTossCompleted
            ? store.manualPaidDttm || ourCompany.manualPaidDttm
            : null,
        userGiftNos: store.userGiftNos,
        payoutType,
        // voucherCompAmount: 0,
        // couponCompAmount: 0,
        ...compFieldsOf(store),
        partiallyDone: (storeDone || ourCompanyDone) && !bothDone,
      });
    } else {
      group.forEach((p) => {
        if (p.store?.storeName === OUR_COMPANY_STORE) return;
        batches.push({
          storeNo: p.storeNo,
          storeName: p.store?.storeName,
          accountHolder: p.store?.accountHolder,
          bankName: p.store?.bankName,
          accountNo: p.store?.accountNo,
          payoutMonth: p.payoutMonth,
          payoutPeriodKey: p.payoutPeriodKey,
          tossPayoutDate: p.tossPayoutDate,
          payoutNo: p.payoutNo,
          totalAmount: p.totalAmount,
          refundFaceAmount: p.payoutType === "CLAWBACK" ? p.totalAmount ?? 0 : 0,
          ourCompanyAmount: 0,
          storeAmount: p.amount,
          tossCharge: p.tossCharge,
          tossStatus: p.tossStatus,
          tossCompletedDttm: p.tossCompletedDttm,
          manualPaidDttm: p.manualPaidDttm,
          userGiftNos: p.userGiftNos,
          payoutType: p.payoutType,
          // voucherCompAmount: 0,
          // couponCompAmount: 0,
          ...compFieldsOf(p),
          partiallyDone: false,
        });
      });
    }
  });

  compPayouts.forEach((p) => {
    batches.push({
      storeNo: p.storeNo,
      storeName: p.store?.storeName,
      accountHolder: p.store?.accountHolder,
      bankName: p.store?.bankName,
      accountNo: p.store?.accountNo,
      payoutMonth: p.payoutMonth,
      payoutPeriodKey: p.payoutPeriodKey,
      tossPayoutDate: p.tossPayoutDate,
      payoutNo: p.payoutNo,
      totalAmount: 0,
      refundFaceAmount: 0,
      ourCompanyAmount: 0,
      storeAmount: p.amount,
      tossCharge: p.tossCharge ?? 0,
      tossStatus: p.tossStatus,
      tossCompletedDttm: p.tossCompletedDttm,
      manualPaidDttm: p.manualPaidDttm,
      userGiftNos: p.userGiftNos ?? [],
      payoutType: p.payoutType,
      // [2026-10-20] 매장 검증 쿠폰 보전 행은 데몬이 comp_* 를 채워둔다(발행주체·쿠폰 내역 포함)
      ...(isOfflineCompPayout(p)
        ? compFieldsOf(p)
        : {
            voucherCompAmount: p.payoutType === COMP_PAYOUT_TYPES.VOUCHER ? Number(p.amount ?? 0) : 0,
            couponCompAmount: p.payoutType === COMP_PAYOUT_TYPES.COUPON ? Number(p.amount ?? 0) : 0,
            // 일회성 보전 행은 발행주체 정보가 없다
            channelCompAmount: 0,
            platformCompAmount: 0,
            storeCompAmount: 0,
            compDetail: [],
          }),
      partiallyDone: false,
    });
  });

  const byPeriod = new Map();
  batches.forEach((b) => {
    const key = `${b.storeNo}::${b.payoutPeriodKey ?? ""}`;
    if (!byPeriod.has(key)) byPeriod.set(key, []);
    byPeriod.get(key).push(b);
  });

  const sum = (list, pick) => list.reduce((s, b) => s + (pick(b) ?? 0), 0);
  const isDone = (b) => b.tossStatus === "COMPLETED" || !!b.manualPaidDttm;
  // 거래(기프트) 단위 건수 — 넷팅된 송금 행이 아니라 그 안에 묶인 개별 user_gift 개수.
  const giftCountOf = (b) => (Array.isArray(b.userGiftNos) ? b.userGiftNos.length : 0);
  const refundCountOf = (b) => (b.payoutType === "CLAWBACK" ? giftCountOf(b) : 0);
  const refundAmountOf = (b) => (b.payoutType === "CLAWBACK" ? Math.abs(b.storeAmount ?? 0) : 0);

  const rows = [];
  byPeriod.forEach((group) => {
    if (group.length === 1) {
      rows.push({
        ...group[0],
        batches: group,
        merged: false,
        giftCount: giftCountOf(group[0]),
        offlineCouponCount: offlineCouponCountOf(group[0].compDetail), // [2026-10-20]
        refundCount: refundCountOf(group[0]),
        refundAmount: refundAmountOf(group[0]),
        // 토스 기준 순액 = 넷팅 순액 − 보전분 (입금대사·토스 정산과 맞춰볼 때 쓰는 값)
        baseAmount:
          (group[0].storeAmount ?? 0) -
          (group[0].voucherCompAmount ?? 0) -
          (group[0].couponCompAmount ?? 0),
      });
      return;
    }
    const allDone = group.every(isDone);
    const anyDone = group.some(isDone);
    const anyTossCompleted = group.some((b) => b.tossStatus === "COMPLETED");
    rows.push({
      storeNo: group[0].storeNo,
      storeName: group[0].storeName,
      accountHolder: group[0].accountHolder,
      bankName: group[0].bankName,
      accountNo: group[0].accountNo,
      payoutMonth: [...new Set(group.map((b) => b.payoutMonth).filter(Boolean))].join(" + "),
      payoutPeriodKey: group[0].payoutPeriodKey,
      tossPayoutDate: group[0].tossPayoutDate,
      totalAmount: sum(group, (b) => b.totalAmount),
      refundFaceAmount: sum(group, (b) => b.refundFaceAmount),
      ourCompanyAmount: sum(group, (b) => b.ourCompanyAmount),
      storeAmount: sum(group, (b) => b.storeAmount),
      tossCharge: sum(group, (b) => b.tossCharge),
      tossStatus: allDone && anyTossCompleted ? "COMPLETED" : null,
      tossCompletedDttm: allDone
        ? group.find((b) => b.tossCompletedDttm)?.tossCompletedDttm
        : null,
      manualPaidDttm:
        allDone && !anyTossCompleted
          ? group.find((b) => b.manualPaidDttm)?.manualPaidDttm
          : null,
      userGiftNos: group.flatMap((b) => b.userGiftNos ?? []),
      giftCount: sum(group, giftCountOf),
      offlineCouponCount: sum(group, (b) => offlineCouponCountOf(b.compDetail)), // [2026-10-20]
      refundCount: sum(group, refundCountOf),
      refundAmount: sum(group, refundAmountOf),
      voucherCompAmount: sum(group, (b) => b.voucherCompAmount),
      couponCompAmount: sum(group, (b) => b.couponCompAmount),
      channelCompAmount: sum(group, (b) => b.channelCompAmount),
      platformCompAmount: sum(group, (b) => b.platformCompAmount),
      storeCompAmount: sum(group, (b) => b.storeCompAmount),
      compDetail: group.flatMap((b) => b.compDetail ?? []),
      baseAmount:
        sum(group, (b) => b.storeAmount) -
        sum(group, (b) => b.voucherCompAmount) -
        sum(group, (b) => b.couponCompAmount),
      batches: group,
      merged: true,
      partiallyDone: (anyDone && !allDone) || group.some((b) => b.partiallyDone),
    });
  });

  return rows;
}

/**
 * [2026-09-30] 정산명세서(LMS) 금액 내역 — 한 상점의 payout 원본 행들을 집계배치(payoutPeriodKey)별로
 *   총결제금액 + 바우처할인 + 쿠폰할인 − 구매자 환불 − 수신자 환불 (− 상점주 환불) − 수수료 = 지급금액
 * 으로 나눈다.
 *  - 총결제금액: NORMAL 행에 묶인 기프트 결제액 합
 *  - 환불: CLAWBACK(정산 후 환불 회수) 행에 묶인 기프트 결제액을 refund.applicant_type 으로 구분
 *    (gifter=구매자 / giftee=수신자 / owner·미상=상점주·기타). 정산 전에 환불된 건은 애초에 정산
 *    대상이 아니라 여기 안 나온다.
 *  - 바우처·쿠폰할인: VOUCHER/COUPON 보전 행 금액
 *  - 수수료: 총결제 − 환불 − (NORMAL+CLAWBACK 실제 정산액) 으로 역산 → 지급금액이 넷팅 순액과 항상 일치
 * 오스리움 수수료 행(오스리움 + tossCharge 0 + 보전 아님)은 상점 몫이 아니라 제외한다.
 */
export function buildStatementBreakdown(payouts) {
  const byNo = new Map();
  for (const p of payouts) {
    const prev = byNo.get(p.payoutNo);
    if (!prev || (!(prev.userGifts?.length) && p.userGifts?.length)) byNo.set(p.payoutNo, p);
  }
  const isFeeRow = (p) =>
    // !isCompPayout(p) && p.store?.storeName === OUR_COMPANY_STORE && Number(p.tossCharge || 0) === 0;
    !isCompPayout(p) &&
    !isOfflineCompPayout(p) &&
    p.store?.storeName === OUR_COMPANY_STORE &&
    Number(p.tossCharge || 0) === 0;
  const faceOf = (p) =>
    Array.isArray(p.userGifts) && p.userGifts.length
      ? p.userGifts.reduce((s, g) => s + Number(g.amount ?? 0), 0)
      : Number(p.totalAmount ?? 0);

  const groups = new Map();
  for (const p of byNo.values()) {
    if (isFeeRow(p)) continue;
    const key = p.payoutPeriodKey ?? "";
    if (!groups.has(key)) {
      groups.set(key, {
        payoutPeriodKey: p.payoutPeriodKey ?? null,
        tossPayoutDate: p.tossPayoutDate ?? null,
        gross: 0,
        voucher: 0,
        coupon: 0,
        refundBuyer: 0,
        refundReceiver: 0,
        refundOther: 0,
        usedCount: 0, // 사용금액(NORMAL) 기프트 건수
        refundBuyerCount: 0,
        refundReceiverCount: 0,
        refundOtherCount: 0,
        settledNet: 0, // NORMAL + CLAWBACK 실제 정산액(수수료 차감 후, 보전분 제외)
        compChannel: 0, // 바우처+쿠폰할인 중 채널 발행분 (데몬 기록분만)
        compPlatform: 0, // 바우처+쿠폰할인 중 플랫폼 발행분 (데몬 기록분만)
        compStore: 0, // 상점 자체 발행 할인액 — 보전 아님(voucher/coupon·지급금액에 미포함), 기록용
        offlineCoupon: 0, // [2026-10-20] 쿠폰할인(coupon) 중 매장 검증(오프라인) 쿠폰 보전분
        offlineCouponCount: 0,
        compDetail: [],
        allDone: true,
      });
    }
    const g = groups.get(key);
    if (!g.tossPayoutDate && p.tossPayoutDate) g.tossPayoutDate = p.tossPayoutDate;
    if (!(p.tossStatus === "COMPLETED" || p.manualPaidDttm)) g.allDone = false;
    // const amount = Number(p.amount ?? 0);
    // [2026-10-20] NORMAL/CLAWBACK 행 amount 에 데몬이 얹은 보전분은 빼서 바우처·쿠폰할인 줄로 옮긴다 —
    // 안 빼면 settledNet 에 섞여 수수료(역산)가 그만큼 작게 나온다.
    const comp = isCompPayout(p) ? null : compFieldsOf(p);
    const amount =
      Number(p.amount ?? 0) - (comp ? comp.voucherCompAmount + comp.couponCompAmount : 0);
    if (comp) {
      g.voucher += comp.voucherCompAmount;
      g.coupon += comp.couponCompAmount;
      g.compChannel += comp.channelCompAmount;
      g.compPlatform += comp.platformCompAmount;
      g.compStore += comp.storeCompAmount;
      g.compDetail.push(...comp.compDetail);
    }
    if (p.payoutType === COMP_PAYOUT_TYPES.VOUCHER) g.voucher += amount;
    else if (p.payoutType === COMP_PAYOUT_TYPES.COUPON) g.coupon += amount;
    else if (isOfflineCompPayout(p)) {
      // [2026-10-20] 보전분은 위 comp 에서 coupon 으로 이미 옮겼다(amount 는 0) — 건수·금액만 따로 표시
      g.offlineCoupon += comp ? comp.couponCompAmount : 0;
      g.offlineCouponCount += offlineCouponCountOf(comp ? comp.compDetail : []);
    }
    else if (p.payoutType === "CLAWBACK") {
      g.settledNet += amount;
      const gifts = Array.isArray(p.userGifts) ? p.userGifts : [];
      if (gifts.length === 0) {
        g.refundOther += Number(p.totalAmount ?? 0);
        g.refundOtherCount += Array.isArray(p.userGiftNos) ? p.userGiftNos.length : 0;
      }
      for (const ug of gifts) {
        const face = Number(ug.amount ?? 0);
        if (ug.refundApplicantType === "gifter") {
          g.refundBuyer += face;
          g.refundBuyerCount += 1;
        } else if (ug.refundApplicantType === "giftee") {
          g.refundReceiver += face;
          g.refundReceiverCount += 1;
        } else {
          g.refundOther += face;
          g.refundOtherCount += 1;
        }
      }
    } else {
      g.settledNet += amount;
      g.gross += faceOf(p);
      g.usedCount +=
        Array.isArray(p.userGifts) && p.userGifts.length
          ? p.userGifts.length
          : Array.isArray(p.userGiftNos)
            ? p.userGiftNos.length
            : 0;
    }
  }

  const finish = (g) => {
    const refund = g.refundBuyer + g.refundReceiver + g.refundOther;
    return {
      ...g,
      fee: g.gross - refund - g.settledNet,
      payout: g.settledNet + g.voucher + g.coupon,
    };
  };
  const sections = [...groups.values()]
    .map(finish)
    .sort((a, b) => (a.payoutPeriodKey || "").localeCompare(b.payoutPeriodKey || ""));

  const sumKeys = [
    "gross", "voucher", "coupon", "refundBuyer", "refundReceiver", "refundOther", "settledNet",
    "usedCount", "refundBuyerCount", "refundReceiverCount", "refundOtherCount",
    "compChannel", "compPlatform", "compStore", "offlineCoupon", "offlineCouponCount",
  ];
  const total = finish(
    sections.reduce(
      (acc, s) => {
        sumKeys.forEach((k) => (acc[k] += s[k]));
        acc.compDetail.push(...s.compDetail);
        acc.allDone = acc.allDone && s.allDone;
        return acc;
      },
      { payoutPeriodKey: null, tossPayoutDate: null, allDone: true, compDetail: [], ...Object.fromEntries(sumKeys.map((k) => [k, 0])) },
    ),
  );
  return { sections, total };
}
